import { defineStore } from "pinia"
import { ref, type Ref } from "vue"
import { useConfigStore } from "@/stores/config"
import { type ImportantModalProps } from "@/features/brain/legacy/components/ImportantModal/type"
import { IMPORTANT_MODALS } from "@/features/brain/legacy/components/ImportantModal/items"

export const useImportantModalStore = defineStore("importatModal", () => {
  const { urls } = useConfigStore()
  const isActive = ref(false)

  const timeout: Ref<ImportantModalProps["timeout"]> = ref(null)
  const icon: Ref<ImportantModalProps["icon"]> = ref("")
  const illustration: Ref<ImportantModalProps["illustration"]> = ref("")
  const title: Ref<ImportantModalProps["title"]> = ref("")
  const subtitle: Ref<ImportantModalProps["subtitle"]> = ref("")
  const description: Ref<ImportantModalProps["description"]> = ref("")
  const subDescription: Ref<ImportantModalProps["subDescription"]> = ref("")
  const textPrimary: Ref<ImportantModalProps["textPrimary"]> = ref("")
  const actionPrimary: Ref<ImportantModalProps["actionPrimary"]> = ref(() => {})
  const textSecondary: Ref<ImportantModalProps["textSecondary"]> = ref("")
  const actionSecondary: Ref<ImportantModalProps["actionSecondary"]> = ref(null)

  function modalTimeout() {
    if (timeout.value) {
      setTimeout(() => {
        isActive.value = false
        timeout.value = null
      }, timeout.value)
    }
  }

  function openModal(
    modal: keyof typeof IMPORTANT_MODALS,
    options: Pick<
      ImportantModalProps,
      | "textPrimary"
      | "actionPrimary"
      | "textSecondary"
      | "actionSecondary"
      | "timeout"
    >
  ) {
    const item = IMPORTANT_MODALS[modal]
    timeout.value = item.timeout || null
    icon.value = item.icon
    illustration.value = item.illustration
      ? urls.assets + item.illustration
      : item.illustration
    title.value = item.title
    subtitle.value = item.subtitle
    description.value = item.description
    subDescription.value = item.subDescription
    isActive.value = true

    // override default values if provided in options
    if (options) {
      timeout.value = options.timeout || item.timeout || null
      textPrimary.value = options.textPrimary || item.textPrimary
      textSecondary.value = options.textSecondary || item.textSecondary

      // ImportantModal are not closable by default
      // so we need to provide a way to close them in primary and secondary actions
      actionPrimary.value = options.actionPrimary
        ? () => {
            options.actionPrimary && options.actionPrimary()
            isActive.value = false
          }
        : null

      actionSecondary.value = options.actionSecondary
        ? () => {
            options.actionSecondary && options.actionSecondary()
            isActive.value = false
          }
        : null
    }

    modalTimeout()
  }

  return {
    isActive,
    timeout,
    icon,
    illustration,
    title,
    subtitle,
    description,
    subDescription,
    textPrimary,
    actionPrimary,
    textSecondary,
    actionSecondary,
    openModal,
  }
})
